'use client';

import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import {
  ExpandableCardGrid,
  type ExpandableCardItem,
} from '@/components/ui/ExpandableCard';
import R2Image from '@/components/ui/R2Image';
import RichText from '@/components/ui/RichText/RichText';
import SectionGrid from '@/components/ui/SectionGrid/SectionGrid';
import type {
  ButtonBlock,
  ImageBlock,
  TextBlock,
} from '@/types/pageContentTypes';

type ImageContentCard = {
  imgUrl?: string;
  imageAlt?: string;
  title: TextBlock;
  description: TextBlock;
};

type ImageContentItem = {
  image?: ImageBlock;
  heading?: TextBlock;
  description?: TextBlock;
};

export interface ImageContentCardsSectionData {
  heading?: TextBlock;
  description?: TextBlock;
  cards?: ImageContentCard[];
  items?: ImageContentItem[];
  btnText?: string;
  button?: ButtonBlock;
}

interface ImageContentCardsSectionProps {
  data: ImageContentCardsSectionData;
  sectionSpacingClassName?: string;
  gridAlignment?: 'start' | 'stretch';
  imageClassName?: string;
  /** Optional classes merged onto each non-expandable card container. */
  cardClassName?: string;
  buttonMarginClassName?: string;
  backgroundColor?: string;
  expandable?: boolean;
  columns?: 2 | 3;
  maxCollapsedHeight?: number;
}

const gridAlignmentClasses = {
  start: 'items-start',
  stretch: 'items-stretch',
};

export default function ImageContentCardsSection({
  data,
  sectionSpacingClassName,
  gridAlignment = 'stretch',
  imageClassName = '',
  cardClassName = '',
  buttonMarginClassName = 'my-8 md:my-10',
  backgroundColor = 'bg-gray-100',
  expandable = false,
  columns = 3,
}: ImageContentCardsSectionProps) {
  if (!data) return null;

  const cards: ImageContentCard[] =
    data.cards ??
    data.items?.map((item) => ({
      imgUrl: item.image?.urlDesktop || item.image?.urlMobile,
      imageAlt: item.image?.alt,
      title: item.heading ?? { content: '' },
      description: item.description ?? { content: '' },
    })) ??
    [];

  const expandableCards: ExpandableCardItem[] = cards.map((card, index) => ({
    id: `${card.title.content || 'card'}-${index}`,
    imageUrl: card.imgUrl,
    imageAlt: card.imageAlt || card.title.content || 'Service image',
    title: <RichText styles={card.title.styles}>{card.title.content}</RichText>,
    description: (
      <RichText styles={card.description.styles}>
        {card.description.content}
      </RichText>
    ),
    alwaysExpandable: false,
  }));

  const sectionButton = data.button;
  const footerButton = data.btnText
    ? {
        content: data.btnText,
        title: data.btnText,
        functionality: 'book_appointment' as const,
      }
    : undefined;

  return (
    <>
      <SectionGrid
        gridClassName="grid-cols-1"
        spacingClassName={sectionSpacingClassName}
        backgroundColor={backgroundColor}
      >
        {(data.heading || data.description) && (
          <div className="tablet:mb-10 laptop:mb-12 mx-auto mb-8 max-w-3xl text-center">
            {data.heading && (
              <RichText styles={data.heading.styles}>
                {data.heading.content}
              </RichText>
            )}
            {data.description && (
              <RichText styles={data.description.styles}>
                {data.description.content}
              </RichText>
            )}
          </div>
        )}

        {expandable ? (
          <ExpandableCardGrid
            cards={expandableCards}
            clampLines={6}
            columns={{ base: 1, md: columns, lg: columns }}
            maxCollapsedHeight={{
              base: 440,
              md: 450,
              lg: 496,
            }}
            cardClassName="bg-white shadow-[0_10px_30px_rgba(0,0,0,0.12)]"
          />
        ) : (
          <div
            className={`grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-3 ${gridAlignmentClasses[gridAlignment]}`}
          >
            {cards.map((card, index) => (
              <div
                key={index}
                className={`flex h-full flex-col gap-4 rounded-lg bg-white p-4 shadow-[0_10px_30px_rgba(0,0,0,0.12)] ${cardClassName}`}
              >
                {card.imgUrl && (
                  <R2Image
                    path={card.imgUrl}
                    alt={card.imageAlt || card.title.content}
                    width={420}
                    height={300}
                    className={`tablet:h-60 laptop:h-61.25 h-56 w-full object-cover ${imageClassName}`}
                  />
                )}
                <RichText styles={card.title.styles}>
                  {card.title.content}
                </RichText>
                <RichText styles={card.description.styles}>
                  {card.description.content}
                </RichText>
              </div>
            ))}
          </div>
        )}

        {sectionButton && (
          <div
            className={`flex w-full justify-center ${buttonMarginClassName}`}
            data-hide-floating-actions="true"
          >
            <PrimaryButton
              data={sectionButton}
              className="w-fit text-base! font-bold"
              widthFitContent
            />
          </div>
        )}
      </SectionGrid>

      {footerButton && (
        <div
          className={`flex w-full justify-center ${buttonMarginClassName}`}
          data-hide-floating-actions="true"
        >
          <PrimaryButton
            data={footerButton}
            className="w-fit text-base! font-bold"
            widthFitContent
          />
        </div>
      )}
    </>
  );
}
